import { ComponentProps, useMemo } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

import { Stack, Typography } from '@mui/material';
import {
    DatePicker,
    DateTimePickerSlotsComponentsProps,
    LocalizationProvider,
    MobileDateTimePicker,
    PickersLocaleText,
} from '@mui/x-date-pickers';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import { useTranslation } from 'react-i18next';

export type DatePickerControllerProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends Path<TFieldValues> = Path<TFieldValues>
> = ComponentProps<typeof DatePicker> & {
    name: TName;
    control: Control<TFieldValues>;
};

const DateTimePickerController = <T,>({
    control,
    name,
    ...props
}: DatePickerControllerProps<T>) => {
    const { t } = useTranslation('translation', {
        keyPrefix: 'DATE_TIME_PICKER',
    });

    const localeText: Partial<PickersLocaleText<dayjs.Dayjs>> = useMemo(
        () => ({
            okButtonLabel: t('OK'),
            cancelButtonLabel: t('CANCEL'),
        }),
        [t]
    );

    const slotProps: DateTimePickerSlotsComponentsProps<unknown> = useMemo(
        () => ({
            toolbar: {
                hidden: true,
            },
        }),
        []
    );

    return (
        <Controller
            control={control}
            name={name}
            render={({
                field: { ref, value, onChange, onBlur },
                fieldState: { error },
            }) => (
                <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="hu"
                    localeText={localeText}
                >
                    <Stack flexDirection="column" gap="6px">
                        <MobileDateTimePicker
                            {...props}
                            sx={{ backgroundColor: 'white' }}
                            inputRef={ref}
                            format="YYYY.MM.DD HH:mm"
                            value={value}
                            onChange={onChange}
                            onClose={onBlur}
                            slotProps={slotProps}
                            defaultValue={value}
                        />
                        <Typography variant="small" color="error">
                            {error?.message}
                        </Typography>
                    </Stack>
                </LocalizationProvider>
            )}
        />
    );
};

export default DateTimePickerController;
